import type {
  AttendeeLearningRecord,
  AttendeeLearningDraftAssistance,
  AttendeeLearningEvidencePackage,
  AttendeeLearningRecordResponse,
  AttendeeLearningRecordValues,
  AttendeeLearningStatementReference,
  PublicConventionGuide,
  PublicScheduleItem,
} from "@verity-connect/contracts";
import { useEffect, useState, type KeyboardEvent } from "react";
import type { LocalPrivateNote } from "./private-notes";
import { markerLabel } from "./private-notes";
import { learningRecordCoverage, reconcileLearningStatementReferences, seedLearningRecordValues } from "./learning-record";
import { SchoolLearningDisclosureNotice } from "./SchoolLearningDisclosureNotice";
import { LearningDocumentActions, learningRecordPlainText } from "./LearningDocumentActions";
import { Link } from "./router";
import { SessionResources } from "./SessionResources";
import type { ReloadableSessionResourcesState, SessionResource } from "./useSessionResources";

export function LearningRecord({ guide, item, note, resourceState, resources, signedIn }: {
  guide: PublicConventionGuide;
  item: PublicScheduleItem | null;
  note: LocalPrivateNote | undefined;
  resourceState: ReloadableSessionResourcesState;
  resources: readonly SessionResource[];
  signedIn: boolean;
}) {
  const [record, setRecord] = useState<AttendeeLearningRecord | null>(null);
  const [evidence, setEvidence] = useState<AttendeeLearningEvidencePackage | null>(null);
  const [draftAssistance, setDraftAssistance] = useState<AttendeeLearningDraftAssistance | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "unavailable">("idle");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AttendeeLearningRecordValues | null>(null);
  const [draftReferences, setDraftReferences] = useState<readonly AttendeeLearningStatementReference[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [draftMessage, setDraftMessage] = useState<string | null>(null);
  const [requestingDraft, setRequestingDraft] = useState(false);

  useEffect(() => {
    document.title = item ? `${item.title} · Learning record · ${guide.event.name}` : `Learning record · ${guide.event.name}`;
  }, [guide.event.name, item]);

  useEffect(() => {
    if (!signedIn || !item) {
      setRecord(null);
      setEvidence(null);
      setDraftAssistance(null);
      setLoadState("idle");
      return;
    }
    const controller = new AbortController();
    setLoadState("loading");
    void fetch(learningRecordPath(guide.event.slug, item.id), { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Your saved learning record could not be loaded.");
        const result = await response.json() as AttendeeLearningRecordResponse;
        setRecord(result.record);
        setEvidence(result.evidence);
        setDraftAssistance(result.draftAssistance);
        setLoadState("ready");
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadState("unavailable");
      });
    return () => controller.abort();
  }, [guide.event.slug, item?.id, loadAttempt, note?.serverRevision, signedIn]);

  useEffect(() => {
    const state = draftAssistance?.request?.state;
    if (!item || (state !== "queued" && state !== "processing") && draftAssistance?.sharedAnalysis?.state !== "pending") return;
    const controller = new AbortController();
    const timer = window.setInterval(() => {
      void fetch(learningRecordPath(guide.event.slug, item.id), { credentials: "same-origin", signal: controller.signal })
        .then(async (response) => {
          if (!response.ok) throw new Error();
          const result = await response.json() as AttendeeLearningRecordResponse;
          setRecord(result.record);
          setEvidence(result.evidence);
          setDraftAssistance(result.draftAssistance);
        })
        .catch(() => { if (!controller.signal.aborted) setDraftMessage("Your work is safe. This page will update when your connection returns."); });
    }, 1_500);
    return () => { window.clearInterval(timer); controller.abort(); };
  }, [draftAssistance?.request?.state, draftAssistance?.request?.updatedAt, draftAssistance?.sharedAnalysis?.state, item?.id]);

  if (!item) {
    return (
      <div className="page learning-record-page">
        <section className="personal-empty">
          <h1>That learning record is not available.</h1>
          <Link className="primary-action" href="/my-wvcea">Return to My WVCEA</Link>
        </section>
      </div>
    );
  }

  const block = guide.blocks.find((candidate) => candidate.id === item.blockId);
  const room = guide.rooms.find((candidate) => candidate.id === item.roomId);
  const presenters = item.presenterIds
    .map((id) => guide.presenters.find((candidate) => candidate.id === id)?.displayName)
    .filter((name): name is string => Boolean(name));
  const hasNote = Boolean(note && (note.values.body.trim() || note.values.markers.some((marker) => marker.text.trim())));
  const hasTranscript = Boolean(evidence?.sources.some((source) => source.kind === "recording_transcript" && source.text !== null));
  const coverage = learningRecordCoverage({
    hasPrivateNote: hasNote,
    hasOfficialDescription: Boolean(item.description?.trim()),
    presenterResourceCount: resources.length,
    hasRecordingTranscript: hasTranscript,
  });
  const details = [block ? `${formatTime(block.startTime)}–${formatTime(block.endTime)}` : null, room?.code, presenters.join(", ")]
    .filter(Boolean)
    .join(" · ");
  const seededValues = seedLearningRecordValues(note?.values ?? { body: "", markers: [] });
  const displayedValues = record?.values ?? seededValues;
  const canShape = signedIn && (record !== null || hasNote || evidence?.coverage.draftAllowed === true);

  const beginEditing = () => {
    setDraft(copyValues(displayedValues));
    setDraftReferences(record?.sources.statements ?? []);
    setSaveMessage(null);
    setEditing(true);
  };

  const saveRecord = async () => {
    if (!item || !draft || saving) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const response = await fetch(learningRecordPath(guide.event.slug, item.id), {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          id: crypto.randomUUID(),
          baseRevision: record?.revision ?? 0,
          values: cleanValues(draft),
          sourceReferences: reconcileLearningStatementReferences(cleanValues(draft), draftReferences),
        }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(detail?.message ?? "This learning record could not be saved.");
      }
      const saved = await response.json() as AttendeeLearningRecord;
      setRecord(saved);
      setEditing(false);
      setSaveMessage("Saved to your account.");
    } catch (reason) {
      setSaveMessage(reason instanceof Error ? reason.message : "This learning record could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const requestDraft = async () => {
    if (!item || requestingDraft) return;
    setRequestingDraft(true);
    setDraftMessage(null);
    try {
      const response = await fetch(`${learningRecordPath(guide.event.slug, item.id)}/draft`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(detail?.message ?? "The app cannot write your session record right now.");
      }
      setDraftAssistance(await response.json() as AttendeeLearningDraftAssistance);
    } catch (reason) {
      setDraftMessage(reason instanceof Error ? reason.message : "The app cannot write your session record right now.");
    } finally {
      setRequestingDraft(false);
    }
  };

  /*
   * The companion the software wrote, in the shape this page already knows how to draw.
   *
   * Only a finished one counts: a request still queued or failed has no words in it, and a
   * page that drew an empty document would be worse than the prompt it replaced.
   */
  const preparedValues = draftAssistance?.request?.state === "ready" ? draftAssistance.request.values : null;
  const written: { values: AttendeeLearningRecordValues; sources: AttendeeLearningRecord["sources"]; updatedAt: string } | null = preparedValues
    ? {
      values: preparedValues,
      // The citations it actually made. The rest of the shape describes a saved record's
      // provenance, which this one does not have yet and must not pretend to.
      sources: {
        noteRevision: null,
        officialDescriptionIncluded: false,
        presenterResourceIds: [],
        statements: draftAssistance?.request?.sourceReferences ?? [],
        capturedAt: draftAssistance?.request?.updatedAt ?? "",
      },
      updatedAt: draftAssistance?.request?.updatedAt ?? "",
    }
    : null;

  /** Whether this page has a finished document to put in front of her. */
  const showing = Boolean(record || preparedValues) && !editing;

  const reviewPreparedDraft = () => {
    const values = draftAssistance?.request?.values;
    if (!values) return;
    setDraft(copyValues(values));
    setDraftReferences(draftAssistance?.request?.sourceReferences ?? []);
    setDraftMessage("Nothing is saved yet. Read it through and change anything you want.");
    setSaveMessage(null);
    setEditing(true);
  };

  const noteBlock = !hasNote && !signedIn ? (
    <section className="personal-empty notes-empty">
      <span className="empty-icon" aria-hidden="true">✎</span>
      <h2>Start with what you want to remember.</h2>
      <p>Your note, key ideas, questions, and next actions will form this session record.</p>
      <Link className="primary-action" href={`/schedule/${encodeURIComponent(item.id)}`}>Start a private note</Link>
    </section>
  ) : hasNote ? (
    <details className="learning-note" open={!record}>
      <summary className="learning-context-summary">
        <div><p>Your private record</p><h2 id="learning-note-title">What I captured</h2></div>
      </summary>
      <div className="learning-context-body">
        <Link className="learning-context-edit" href={`/schedule/${encodeURIComponent(item.id)}?view=notes`}>Edit note</Link>
        {note?.values.body.trim() && <p className="learning-note-body">{note.values.body}</p>}
        {note && note.values.markers.some((marker) => marker.text.trim()) && (
          <div className="learning-marker-list">
            {note.values.markers.filter((marker) => marker.text.trim()).map((marker) => (
              <div className={`learning-marker ${marker.kind}`} key={marker.id}>
                <small>{markerLabel(marker.kind)}</small>
                <p>{marker.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  ) : null;

  return (
    <article className="page learning-record-page">
      {/*
        * The way out. Opening a companion from the list left a teacher on a page with no
        * way back to it: the tab bar moves her elsewhere entirely and the browser's own
        * back gesture is not a thing an installed application has. She was stuck.
        */}
      <Link className="back-link subpage-back" href="/my-wvcea/learning">← Learning records</Link>
      <header className="learning-record-hero">
        <p>Learning Record</p>
        <h1 data-page-heading tabIndex={-1}>{item.title}</h1>
        {details && <span>{details}</span>}
        {!showing && (
          <div className={`learning-coverage ${coverage.level}`}>
            <strong>{coverage.label}</strong>
            <small>{coverage.sourceCount} {coverage.sourceCount === 1 ? "source" : "sources"} available</small>
          </div>
        )}
      </header>
      {!showing && <SchoolLearningDisclosureNotice eventSlug={guide.event.slug} signedIn={signedIn} />}
      {!showing && noteBlock}

      <section className="learning-shaped-record" aria-labelledby={showing ? undefined : "shaped-record-title"} aria-label={showing ? "Learning record" : undefined}>
        {/*
          * The record, then what she can do with it.
          *
          * A teacher tapping a session met a small label, an Edit button, a row of Copy and
          * Print, a second small label and the time the record was saved, all before the
          * first sentence of the record. Nathan, 26 September: "why wouldn't editing and
          * printing be UNDER the learning record, as options AFTER a teacher reads it?"
          *
          * When there is a document, the session title above is its heading and nothing
          * else stands between it and the words. The header and its offers belong to the
          * case where there is nothing to read yet.
          */}
        {!showing && (
          <header>
            <div>
              <p>From this session</p>
              <h2 id="shaped-record-title">My learning record</h2>
            </div>
            {canShape && !editing && <button type="button" onClick={beginEditing}>Write it myself</button>}
          </header>
        )}

        {/*
          * Nothing about the record above the record: coverage and the offers to write one
          * appear only while there is no document to show.
          */}
        {!showing && evidence && <EvidenceCoverage evidence={evidence} />}

        {!showing && draftAssistance?.enabled && evidence?.coverage.draftAllowed && (
          <LearningDraftHelp
            assistance={draftAssistance}
            message={draftMessage}
            requesting={requestingDraft}
            reviewing={editing}
            onRequest={() => { void requestDraft(); }}
            onReview={reviewPreparedDraft}
          />
        )}

        {!signedIn ? (
          <div className="learning-record-prompt">
            <p>Sign in to save a private session record and open it on your other devices.</p>
            <Link className="primary-action" href="/account">Sign in</Link>
          </div>
        ) : editing && draft ? (
          <LearningRecordEditor
            values={draft}
            saving={saving}
            message={saveMessage}
            onChange={setDraft}
            onCancel={() => { setEditing(false); setSaveMessage(null); }}
            onSave={() => { void saveRecord(); }}
          />
        ) : record ? (
          <>
            <LearningRecordDocument values={record.values} sources={record.sources} itemId={item?.id} />
            <LearningDocumentActions text={learningRecordPlainText(item.title, record.values)} onEdit={canShape ? beginEditing : undefined} />
          </>
        ) : written ? (
          /*
           * What the software wrote, shown as the finished thing it is.
           *
           * It used to sit behind a panel she had to notice, read through and save before
           * this page would show it. That was the right shape when a teacher asked for a
           * draft. It stopped being right when the software began writing one for every
           * teacher who attended a session, unasked and unannounced: a thousand and
           * forty-eight of them exist for this convention and almost none had ever been
           * seen. The man who commissioned the feature did not know the step was there.
           *
           * Her own saved version still wins, above. This is only what she is shown when
           * she has not written one herself.
           */
          <>
            <LearningRecordDocument values={written.values} sources={written.sources} itemId={item?.id} />
            <p className="learning-record-provenance">
              Written for you from this session's material. Change anything you want.
            </p>
            <LearningDocumentActions text={learningRecordPlainText(item.title, written.values)} onEdit={canShape ? beginEditing : undefined} />
          </>
        ) : evidence?.coverage.state === "insufficient" && !hasNote ? (
          <div className="learning-reflection-prompt">
            <p>Add something you want to remember from this session.</p>
            <ul><li>What idea do you want to remember?</li><li>What question or next step is yours?</li></ul>
            <Link className="primary-action" href={`/schedule/${encodeURIComponent(item.id)}?view=notes`}>Add a note</Link>
          </div>
        ) : (
          <div className="learning-record-prompt">
            <p>{loadState === "loading" ? "Checking your account…" : loadState === "unavailable" ? "Your account record is unavailable right now. Your original note is still safe on this device." : "Choose the ideas, questions, and next steps worth carrying with you."}</p>
            {loadState === "unavailable" && <button className="primary-action" type="button" onClick={() => { setLoadState("loading"); setLoadAttempt((value) => value + 1); }}>Try again</button>}
          </div>
        )}
        {saveMessage && !editing && <p className="learning-save-message" role="status">{saveMessage}</p>}
      </section>

      {showing && <SchoolLearningDisclosureNotice eventSlug={guide.event.slug} signedIn={signedIn} />}
      {showing && noteBlock}

      <details className="learning-sources" open={!record}>
        <summary className="learning-context-summary"><div><p>Notes and materials</p><h2 id="learning-sources-title">Where this record came from</h2></div></summary>
        <div className="learning-context-body">
          <div className="learning-source-list">
            <div><span aria-hidden="true">●</span><strong>My private note</strong><small>{hasNote ? "Included" : "Not started"}</small></div>
            <div><span aria-hidden="true">●</span><strong>WVCEA program</strong><small>{item.description ? "Official description included" : "No description was supplied"}</small></div>
            <div><span aria-hidden="true">●</span><strong>Presenter material</strong><small>{resourceState.status === "loading" ? "Checking for files" : resourceState.status === "unavailable" ? "Could not be checked" : resources.length > 0 ? `${resources.length} ${resources.length === 1 ? "item" : "items"} shared; readable content is used when available` : "Nothing has been shared"}</small></div>
            <div><span aria-hidden="true">●</span><strong>Session recording</strong><small>{hasTranscript ? "Transcript included" : signedIn && loadState === "ready" ? "No transcript received" : "Not checked"}</small></div>
          </div>
          {item.description && (
            <details className="learning-program-source">
              <summary>Read the official session description</summary>
              <p>{item.description}</p>
            </details>
          )}
          {resourceState.status === "loading" ? <p className="learning-source-state" role="status">Checking for presenter files…</p>
            : resourceState.status === "unavailable" ? <div className="learning-source-state error" role="status"><span>Presenter files could not be loaded. Your record and notes are safe.</span><button type="button" onClick={resourceState.retry}>Try again</button></div>
              : <SessionResources resources={resources} signedIn={signedIn} eventSlug={guide.event.slug} />}
        </div>
      </details>

      <p className="device-note"><span aria-hidden="true">●</span>This record stays private. WVCEA staff cannot open or read it.</p>
    </article>
  );
}

function LearningDraftHelp({ assistance, message, requesting, reviewing, onRequest, onReview }: {
  assistance: AttendeeLearningDraftAssistance;
  message: string | null;
  requesting: boolean;
  reviewing: boolean;
  onRequest: () => void;
  onReview: () => void;
}) {
  const request = assistance.request;
  const working = request?.state === "queued" || request?.state === "processing";
  const failed = request?.state === "failed";
  const shared = assistance.sharedAnalysis?.state;
  return (
    <div className={`learning-draft-help ${request?.state ?? "available"}`} aria-live="polite">
      <div>
        <small>Help writing this record</small>
        <strong>{request?.state === "ready" ? "A first version is ready"
          : failed ? "Your record could not be written"
          : working ? "Writing your session record"
            : shared === "ready" ? "Use the session materials and your notes"
              : "Let the app write a first version"}</strong>
        <p>{request?.state === "ready"
          ? "Open it, read it through, and change anything you want before you save."
          : failed
            ? request.detail ?? "The app stopped before it finished. Your notes and session materials are still safe."
          : working
            ? "You can leave this page. We will keep working and save the first version to your account."
            : shared === "ready"
              ? "The app will use the session materials for facts and your own words for personal sections."
              : shared === "pending"
                ? "The session materials are still being read."
                : "The app will use only the notes, recording, and session materials available here. You decide what to keep."}</p>
        {message && <span className="learning-draft-message">{message}</span>}
      </div>
      {request?.state === "ready" && request.values && !reviewing
        ? <button type="button" onClick={onReview}>Open first version</button>
        : assistance.canRequest
          ? <button type="button" onClick={onRequest} disabled={requesting}>{requesting ? "Starting…" : failed ? "Try again" : "Create first version"}</button>
          : working ? <span className="learning-draft-working" aria-label="Writing your session record">•••</span> : null}
    </div>
  );
}

function EvidenceCoverage({ evidence }: { evidence: AttendeeLearningEvidencePackage }) {
  const label = evidence.coverage.state === "supported"
    ? "Notes and session materials"
    : evidence.coverage.state === "session_only"
      ? "Session materials are ready"
    : evidence.coverage.state === "personal_only"
      ? "Your notes are ready"
      : "More information needed";
  return (
    <div className={`learning-evidence-gate ${evidence.coverage.state}`}>
      <strong>{label}</strong>
      <span>{evidence.coverage.explanation}</span>
      <small>{evidence.coverage.substantiveSourceCount} useful {evidence.coverage.substantiveSourceCount === 1 ? "item" : "items"}</small>
    </div>
  );
}

export function LearningRecordEditor({ values, saving, message, onChange, onCancel, onSave }: {
  values: AttendeeLearningRecordValues;
  saving: boolean;
  message: string | null;
  onChange: (values: AttendeeLearningRecordValues) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const [section, setSection] = useState<LearningRecordSection>("ideas");
  const set = (field: keyof AttendeeLearningRecordValues, value: string | readonly string[]) => onChange({ ...values, [field]: value });
  return (
    <form className="learning-record-editor" onSubmit={(event) => { event.preventDefault(); onSave(); }}>
      <LearningRecordTabs selected={section} onSelect={setSection} />
      <div className="learning-record-editor-panel" id="learning-record-panel-ideas" role="tabpanel" aria-labelledby="learning-record-tab-ideas" hidden={section !== "ideas"}>
        <label>Central idea<textarea rows={4} maxLength={4000} value={values.centralIdea} onChange={(event) => set("centralIdea", event.target.value)} /></label>
        <label>Core ideas <small>One complete idea per line</small><textarea rows={8} maxLength={24000} value={values.mainPoints.join("\n")} onChange={(event) => set("mainPoints", lines(event.target.value))} /></label>
      </div>
      <div className="learning-record-editor-panel" id="learning-record-panel-details" role="tabpanel" aria-labelledby="learning-record-tab-details" hidden={section !== "details"}>
        <label>Routines and steps <small>One per line</small><textarea rows={7} maxLength={16000} value={values.frameworks.join("\n")} onChange={(event) => set("frameworks", lines(event.target.value))} /></label>
        <label>Examples and stories <small>One per line</small><textarea rows={6} maxLength={32000} value={values.examples.join("\n")} onChange={(event) => set("examples", lines(event.target.value))} /></label>
        <label>Resources and references <small>One per line</small><textarea rows={6} maxLength={20000} value={values.resources.join("\n")} onChange={(event) => set("resources", lines(event.target.value))} /></label>
      </div>
      <div className="learning-record-editor-panel" id="learning-record-panel-classroom" role="tabpanel" aria-labelledby="learning-record-tab-classroom" hidden={section !== "classroom"}>
        <label>Ways to use this in class <small>One idea per line</small><textarea rows={6} maxLength={24000} value={values.practicalStrategies.join("\n")} onChange={(event) => set("practicalStrategies", lines(event.target.value))} /></label>
        <label>Possible next steps <small>One per line</small><textarea rows={3} maxLength={20000} value={values.nextActions.join("\n")} onChange={(event) => set("nextActions", lines(event.target.value))} /></label>
      </div>
      <div className="learning-record-editor-panel" id="learning-record-panel-personal" role="tabpanel" aria-labelledby="learning-record-tab-personal" hidden={section !== "personal"}>
        <label>What connected with me<textarea rows={5} maxLength={8000} value={values.whatStoodOut} onChange={(event) => set("whatStoodOut", event.target.value)} /></label>
        <label>Questions I still have <small>One per line</small><textarea rows={3} maxLength={20000} value={values.questions.join("\n")} onChange={(event) => set("questions", lines(event.target.value))} /></label>
      </div>
      {message && <p className="learning-save-error" role="alert">{message}</p>}
      <footer>
        <button type="button" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="primary-action" type="submit" disabled={saving}>{saving ? "Saving…" : "Save record"}</button>
      </footer>
    </form>
  );
}

export function LearningRecordDocument({ values, sources, itemId }: {
  values: AttendeeLearningRecordValues;
  sources: AttendeeLearningRecord["sources"];
  /**
   * Which session this record is of. Allowed to be absent because the demonstration renders a
   * record with no session behind it, and a citation with nowhere to go is drawn as the plain
   * text it has always been rather than a link that would refuse her.
   */
  itemId?: string | undefined;
}) {
  const [section, setSection] = useState<LearningRecordSection>("ideas");
  return (
    <div className="learning-record-view companion-document">
      <LearningRecordTabs selected={section} onSelect={setSection} />

      <div className="companion-record-panel" id="learning-record-panel-ideas" role="tabpanel" aria-labelledby="learning-record-tab-ideas" hidden={section !== "ideas"}>
        <section className="companion-lead">
          <h3>Central idea</h3>
          {values.centralIdea.trim()
            ? <p>{values.centralIdea}<StatementSource source={statementSourceLabel("Central idea", 0, values.centralIdea, sources.statements)} itemId={itemId} /></p>
            : <p className="companion-missing">No central idea was found in the available material.</p>}
        </section>
        <section className="companion-session-points">
          <header><h3>Core ideas</h3><p>The session's main points in plain language.</p></header>
          {values.mainPoints.some((item) => item.trim())
            ? <ul>{values.mainPoints.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Main points-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Main points", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
            : <p className="companion-missing">No other main ideas were found.</p>}
        </section>
      </div>

      <div className="companion-record-panel" id="learning-record-panel-details" role="tabpanel" aria-labelledby="learning-record-tab-details" hidden={section !== "details"}>
        <div className="companion-detail-grid">
          <section className="frameworks">
            <h3>Routines and steps</h3>
            {values.frameworks.some((item) => item.trim())
              ? <ul>{values.frameworks.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Frameworks-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Routines and steps", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
              : <p className="companion-missing">No routine or set of steps was found.</p>}
          </section>
          <section className="examples">
            <h3>Examples</h3>
            {values.examples.some((item) => item.trim())
              ? <ul>{values.examples.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Examples-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Examples and stories", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
              : <p className="companion-missing">No example or story was found.</p>}
          </section>
        </div>
        <section className="companion-resources-list">
          <h3>Resources mentioned</h3>
          {values.resources.some((item) => item.trim())
            ? <ul>{values.resources.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Resources-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Resources", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
            : <p className="companion-missing">No resource was named in the available material.</p>}
        </section>
      </div>

      <div className="companion-record-panel" id="learning-record-panel-classroom" role="tabpanel" aria-labelledby="learning-record-tab-classroom" hidden={section !== "classroom"}>
        <section className="companion-strategies">
          <header><h3>Ways to use this in class</h3><p>Ideas you may want to try.</p></header>
          {values.practicalStrategies.some((item) => item.trim())
            ? <ul>{values.practicalStrategies.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Strategies-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Ways to use this in class", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
            : <p className="companion-missing">No classroom idea was found.</p>}
        </section>
        <section className="companion-next-actions">
          <header><span aria-hidden="true">→</span><div><h3>Try next</h3><p>One place to start.</p></div></header>
          {values.nextActions.some((item) => item.trim())
            ? <ul>{values.nextActions.filter((item) => item.trim()).map((item, index) => <LearningPoint key={`Possible next steps-${index}`} item={item} label={<StatementSource source={statementSourceLabel("Possible next steps", index, item, sources.statements)} itemId={itemId} />} />)}</ul>
            : <p className="companion-missing">No next step was added.</p>}
        </section>
      </div>

      <div className="companion-record-panel" id="learning-record-panel-personal" role="tabpanel" aria-labelledby="learning-record-tab-personal" hidden={section !== "personal"}>
        <div className="companion-reflection">
          <section>
            <h3>What stood out to me</h3>
            {values.whatStoodOut.trim()
              ? <p>{values.whatStoodOut}<StatementSource source={statementSourceLabel("What stood out", 0, values.whatStoodOut, sources.statements)} itemId={itemId} /></p>
              : <p className="companion-missing">I have not added anything here yet.</p>}
          </section>
          <section>
            <h3>Questions I still have</h3>
            {values.questions.some((item) => item.trim())
              ? <ul>{values.questions.filter((item) => item.trim()).map((item, index) => <li key={`Questions-${index}`}>{item}<StatementSource source={statementSourceLabel("Questions I still have", index, item, sources.statements)} itemId={itemId} /></li>)}</ul>
              : <p className="companion-missing">I have not added a question yet.</p>}
          </section>
        </div>
      </div>
      <footer className="companion-source-note">
        <strong>Based on</strong>
        <span>{sourceSnapshotLabel(sources)}</span>
      </footer>
    </div>
  );
}

type LearningRecordSection = "ideas" | "details" | "classroom" | "personal";
const LEARNING_RECORD_TABS: readonly [LearningRecordSection, string][] = [
  ["ideas", "Key ideas"],
  ["details", "Session details"],
  ["classroom", "For my class"],
  ["personal", "My notes"],
];

/**
 * One point, opened only if there is more of it.
 *
 * A record runs to about eight hundred words, and two thirds of its points used to be a
 * single sentence of around twenty words. Nine of those stacked is the wall of text a
 * teacher meets on her phone, and no amount of cutting words off the front makes a heading
 * out of one: naming what a point is about is a judgement, so the builder makes it.
 *
 * It now leads every point with a short sentence stating the point and explains it after.
 * That opening sentence is the row. The rest opens when she taps, and when there is no rest
 * the row is simply the point, which is also what every record written before this reads as.
 */
function LearningPoint({ item, label }: { item: string; label: React.ReactNode }): React.JSX.Element {
  const [opening, ...rest] = item.split(/(?<=[.!?])\s+/u);
  const body = rest.join(" ").trim();
  if (!body) return <li className="companion-point"><span>{item}</span>{label}</li>;
  return (
    <li className="companion-point companion-point-openable">
      <details>
        <summary><span>{opening}</span></summary>
        <p>{body}{label}</p>
      </details>
    </li>
  );
}

function LearningRecordTabs({ selected, onSelect }: { selected: LearningRecordSection; onSelect: (section: LearningRecordSection) => void }) {
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = LEARNING_RECORD_TABS.length - 1;
    const next = event.key === "ArrowRight" ? index === last ? 0 : index + 1
      : event.key === "ArrowLeft" ? index === 0 ? last : index - 1
        : event.key === "Home" ? 0 : event.key === "End" ? last : null;
    if (next === null) return;
    event.preventDefault();
    const value = LEARNING_RECORD_TABS[next]![0];
    onSelect(value);
    document.getElementById(`learning-record-tab-${value}`)?.focus();
  };
  return <div className="learning-record-tabs" role="tablist" aria-label="Session record sections">{LEARNING_RECORD_TABS.map(([value, label], index) => <button id={`learning-record-tab-${value}`} key={value} type="button" role="tab" aria-selected={selected === value} aria-controls={`learning-record-panel-${value}`} tabIndex={selected === value ? 0 : -1} onKeyDown={(event) => move(event, index)} onClick={() => onSelect(value)}>{label}</button>)}</div>;
}

/**
 * Where a sentence in her record came from, and now a way to go and read it.
 *
 * This has been inert text since it shipped: the statement carries the identifiers of the
 * transcript segments behind it, and the label threw them away one line before they could be
 * used. So a teacher could be told a claim came from the session recording and had no way of
 * seeing the fifteen seconds that produced it.
 *
 * A real control rather than styled text, because it is one — it moves her to another page,
 * and a screen reader and a keyboard both have to be able to find it.
 */
function StatementSource({ source, itemId }: { source: StatementSource | null; itemId: string | undefined }) {
  if (!source) return null;
  if (!source.segmentId || !itemId) {
    return <small className="learning-statement-source">{source.label}</small>;
  }
  return (
    <Link
      className="learning-statement-source learning-statement-source-linked"
      href={`/my-wvcea/learning/${encodeURIComponent(itemId)}/transcript#t-${encodeURIComponent(source.segmentId)}`}
    >
      {source.label}
    </Link>
  );
}

interface StatementSource {
  label: string;
  /** The moment in the transcript this came from, where it came from one. */
  segmentId: string | null;
}

function statementSourceLabel(
  label: string,
  index: number,
  text: string,
  references: readonly AttendeeLearningStatementReference[],
): StatementSource | null {
  const field = label === "Central idea" ? "centralIdea"
    : label === "Main points" ? "mainPoints"
      : label === "Routines and steps" ? "frameworks"
        : label === "Examples and stories" ? "examples"
          : label === "Ways to use this in class" ? "practicalStrategies"
            : label === "Resources" ? "resources"
              : label === "What stood out" ? "whatStoodOut"
                : label === "Questions I still have" ? "questions"
                  : "nextActions";
  const reference = references.find((candidate) => candidate.field === field && candidate.index === index && candidate.text === text.trim());
  if (!reference) return null;
  if (reference.origin === "attendee") return { label: "Added by me", segmentId: null };
  const kinds = new Set(reference.sourceIds.map((id) => id.split(":", 1)[0]));
  // Kept rather than discarded. A transcript source identifier is `kind:item:segment`, and
  // that last part is the fifteen seconds of a fifty-minute talk this sentence came out of.
  const moment = firstTranscriptSegment(reference.sourceIds);
  if (kinds.size > 1) return { label: "Session materials", segmentId: moment };
  if (kinds.has("transcript") || kinds.has("official-transcript")) {
    return { label: "Session recording", segmentId: moment };
  }
  if (kinds.has("presenter")) return { label: "Presenter material", segmentId: null };
  if (kinds.has("attendee")) return { label: "My notes", segmentId: null };
  if (kinds.has("official")) return { label: "Official program", segmentId: null };
  return { label: "Session material", segmentId: null };
}

/**
 * The first moment behind a statement, where one of its sources is a transcript.
 *
 * The first rather than all of them: a sentence drawn from four passages has to land
 * somewhere, and the earliest is the one she can read forward from.
 */
function firstTranscriptSegment(sourceIds: readonly string[]): string | null {
  for (const id of sourceIds) {
    const parts = id.split(":");
    if (parts.length < 3) continue;
    if (parts[0] !== "transcript" && parts[0] !== "official-transcript") continue;
    const segment = parts.slice(2).join(":");
    if (segment) return segment;
  }
  return null;
}

function sourceSnapshotLabel(sources: AttendeeLearningRecord["sources"]): string {
  const labels = [
    sources.noteRevision === null ? null : "your notes",
    sources.officialDescriptionIncluded ? "the official program" : null,
    sources.presenterResourceIds.length > 0
      ? `${sources.presenterResourceIds.length} presenter ${sources.presenterResourceIds.length === 1 ? "item" : "items"}`
      : null,
  ].filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join(" · ") : "No source content was available.";
}

function learningRecordPath(eventSlug: string, itemId: string): string {
  return `/api/attendee/events/${encodeURIComponent(eventSlug)}/learning-records/${encodeURIComponent(itemId)}`;
}

function lines(value: string): readonly string[] {
  return value.split("\n");
}

function cleanValues(values: AttendeeLearningRecordValues): AttendeeLearningRecordValues {
  return {
    centralIdea: values.centralIdea.trim(),
    mainPoints: values.mainPoints.map((value) => value.trim()).filter(Boolean),
    frameworks: values.frameworks.map((value) => value.trim()).filter(Boolean),
    examples: values.examples.map((value) => value.trim()).filter(Boolean),
    practicalStrategies: values.practicalStrategies.map((value) => value.trim()).filter(Boolean),
    resources: values.resources.map((value) => value.trim()).filter(Boolean),
    whatStoodOut: values.whatStoodOut.trim(),
    questions: values.questions.map((value) => value.trim()).filter(Boolean),
    nextActions: values.nextActions.map((value) => value.trim()).filter(Boolean),
  };
}

function copyValues(values: AttendeeLearningRecordValues): AttendeeLearningRecordValues {
  return { ...values, mainPoints: [...values.mainPoints], frameworks: [...values.frameworks], examples: [...values.examples],
    practicalStrategies: [...values.practicalStrategies], resources: [...values.resources],
    questions: [...values.questions], nextActions: [...values.nextActions] };
}

function formatTime(value: string): string {
  const match = value.match(/^(\d{2}):(\d{2})$/u);
  if (!match?.[1] || !match[2]) return value;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const suffix = hours < 12 ? "a.m." : "p.m.";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}
